import { useMutation as useReactMutation, UseMutationResult } from '@tanstack/react-query'
import { AxiosRequestConfig } from 'axios'

import { useClient } from '../useClient/useClient'
import { useCaptcha } from '../useCaptcha/useCaptcha'

import { MutationFn, UseMutationOptions } from './useMutation.types'
// A header, not the body. The API checks the body against a schema that rejects
// unknown fields.
import { CAPTCHA_TOKEN_HEADER } from '@percent/education-hosted-form/constants/captcha'
import { isInvalidCaptchaTokenError } from '@percent/education-hosted-form/utils/isInvalidCaptchaTokenError'

export const useMutation = <TData = unknown, TError = unknown, TVariables = void, TContext = unknown>(
  mutation: MutationFn<TVariables, TData>,
  options?: UseMutationOptions<TData, TError, TVariables, TContext>
): UseMutationResult<TData, TError, TVariables, TContext> => {
  const client = useClient()
  const { getToken: getCaptchaToken, reset: resetCaptcha } = useCaptcha()

  const { captchaAction, ...mutationOptions } = options ?? {}

  return useReactMutation<TData, TError, TVariables, TContext>({
    mutationFn: async variables => {
      const captchaToken = captchaAction ? await getCaptchaToken(captchaAction) : undefined

      // No token means no header. Only the API can enforce.
      const requestConfig: AxiosRequestConfig | undefined = captchaToken
        ? { headers: { [CAPTCHA_TOKEN_HEADER]: captchaToken } }
        : undefined

      try {
        return await mutation(client, variables, requestConfig)
      } catch (error) {
        if (captchaAction && isInvalidCaptchaTokenError(error)) {
          resetCaptcha()
        }

        throw error
      }
    },
    ...mutationOptions
  })
}
